import { PlainButton } from "@jugl-web/ui-components/cross-platform";
import {
  MentionTextarea,
  MentionTextareaHandle,
  MentionTextareaProps,
} from "@jugl-web/ui-components/cross-platform/RichTextarea";
import { assert, onEnter } from "@jugl-web/utils";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as SubmitChecklistIcon } from "../../assets/submit-checklist.svg";
import { TaskTableLayout } from "../TaskTableLayout";

interface TextCellEditorProps
  extends Pick<MentionTextareaProps, "mentions" | "placeholder"> {
  isDisabled?: boolean;
  validator: (rawText: string) => boolean;
  onSubmit: (richText: string) => void;
  onCancel: () => void;
}

export interface TextCellEditorHandle {
  focus: () => void;
  setContent: (richText: string) => void;
}

export const TextCellEditor = forwardRef<
  TextCellEditorHandle,
  TextCellEditorProps
>(
  (
    {
      isDisabled = false,
      validator,
      onSubmit,
      onCancel,
      ...mentionTextareaProps
    },
    ref
  ) => {
    const [isValid, setIsValid] = useState(false);

    const $mentionTextareaRef = useRef<MentionTextareaHandle | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => $mentionTextareaRef.current?.focus(),
      setContent: (richText) => {
        $mentionTextareaRef.current?.setRichText(richText);
        setIsValid(validator(richText));
      },
    }));

    useEffect(() => {
      $mentionTextareaRef.current?.focus();
    }, []);

    return (
      <>
        <MentionTextarea
          ref={$mentionTextareaRef}
          mentions={[]}
          mentionListType="caret-pinned"
          isDisabled={isDisabled}
          onRawTextChange={(rawText) => setIsValid(validator(rawText))}
          onEscape={onCancel}
          classes={{
            container:
              "w-full text-sm font-primary text-dark-800 tracking-[0.14px] py-4 leading-3",
            placeholder: "left-0 top-4 text-grey-background",
            contentEditable: "outline-none p-0 max-h-[80px] overflow-y-auto",
          }}
          {...mentionTextareaProps}
        />
        <div className="flex shrink-0 items-center gap-2">
          <PlainButton
            className="jugl__focusable-outline hover:bg-grey/5 text-messages-chatBubbleSender flex h-[34px] w-[34px] items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:text-[#E0E0E0] disabled:hover:bg-transparent"
            disabled={!isValid || isDisabled}
            onKeyDown={onEnter((event) => event.stopPropagation())}
            onClick={(event) => {
              assert(
                !!$mentionTextareaRef.current,
                "MentionTextarea ref is not set"
              );

              event.stopPropagation();
              onSubmit($mentionTextareaRef.current.getRichText());
            }}
          >
            <SubmitChecklistIcon className="transition-colors" />
          </PlainButton>
          <TaskTableLayout.ClearCellValueButton
            isDisabled={isDisabled}
            onKeyDown={onEnter((event) => event.stopPropagation())}
            onClick={(event) => {
              event.stopPropagation();
              onCancel();
            }}
          />
        </div>
      </>
    );
  }
);
